import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { ApiError } from '../models/base.model';
import { AuthService } from './auth.service';
import {
    SessionExpiryDialogComponent,
    SessionExpiryDialogResult,
} from './session-expiry-dialog/session-expiry-dialog.component';

/**
 * Watches the access token expiry and keeps the session alive.
 *
 *   timer fires at (expiresAt - WARN_LEAD)
 *      |- refresh succeeds -> reschedule, user sees nothing
 *      `- refresh fails    -> show the expiry dialog, counting down to real expiry
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
    /** How long before expiry we try to renew the session. */
    private readonly WarnLeadSeconds = 300;

    /** Delay before the single retry we allow for transient refresh failures. */
    private readonly RetryDelaySeconds = 30;

    private _authSvc = inject(AuthService);
    private _matDialog = inject(MatDialog);
    private _router = inject(Router);

    private _timerSub: Subscription | null = null;
    private _dialogRef: MatDialogRef<SessionExpiryDialogComponent> | null = null;
    private _refreshing: boolean = false;
    private _retried: boolean = false;

    /**
     * Begin watching. Safe to call repeatedly; any pending timer is replaced.
     */
    start(): void {
        this.clearTimers();

        const expiresAt = this._authSvc.getTokenExpiresAt();

        // Not signed in, or no expiry recorded - nothing to watch.
        if (!expiresAt) {
            return;
        }

        const secondsLeft = expiresAt - (new Date().getTime() / 1000);

        // Already expired: an expired access token normally means the refresh token
        // is stale too, and the interceptor would 401 anyway. Sign out now.
        if (secondsLeft <= 0) {
            this.logout();
            return;
        }

        // Within (or past) the lead window but still valid - try to renew immediately.
        const delaySeconds = secondsLeft - this.WarnLeadSeconds;
        const delayMs = delaySeconds > 0 ? delaySeconds * 1000 : 0;

        this._timerSub = timer(delayMs).subscribe(() => this.attemptSilentRefresh());
    }

    /**
     * Stop watching and close the dialog if it is open.
     */
    stop(): void {
        this.clearTimers();
        this._refreshing = false;
        this._retried = false;

        if (this._dialogRef) {
            this._dialogRef.close();
            this._dialogRef = null;
        }
    }

    /**
     * Listen for credential changes so login / refresh / logout reschedule the watchdog.
     * Called once from the app initializer.
     */
    watchAuthCredential(): void {
        this._authSvc.authCredentialChanged$.subscribe((cred) => {
            if (cred) {
                this.stop();
                this.start();
            } else {
                this.stop();
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Renew the session in the background. Only when this fails does the user see anything.
     */
    private attemptSilentRefresh(): void {
        if (this._refreshing) {
            return;
        }
        this._refreshing = true;

        // Deliberately not tracked for unsubscription: the success path calls
        // setAuthCredential() from inside the response pipeline, which re-enters
        // stop() - tearing down our own subscription mid-emission would be fragile.
        // The _refreshing flag prevents overlap instead.
        this._authSvc.refreshAuthToken().subscribe({
            next: () => {
                // setAuthCredential() emits authCredentialChanged$, which reschedules us.
                this._refreshing = false;
                this._retried = false;
            },
            error: (err) => {
                this._refreshing = false;

                // Signed out while the request was in flight - nothing left to warn about.
                if (!this._authSvc.getTokenExpiresAt()) {
                    return;
                }

                if (!this._retried && this.isTransientError(err)) {
                    this._retried = true;
                    this.clearTimers();
                    this._timerSub = timer(this.RetryDelaySeconds * 1000).subscribe(() =>
                        this.attemptSilentRefresh()
                    );
                    return;
                }

                console.error('[SessionExpiry] unable to refresh session:', err);
                this.openExpiryDialog();
            },
        });
    }

    /**
     * Network drop or server hiccup is worth one retry; a 400/401 means the refresh
     * token itself is dead and retrying will never help.
     *
     * RestApiService wraps failures in ApiError, so the HTTP status lives on
     * `httpErrorResponse` - ApiError.code may hold an API-specific code instead.
     */
    private isTransientError(err: any): boolean {
        let status: number | null = null;

        if (err instanceof HttpErrorResponse) {
            status = err.status;
        } else if (err instanceof ApiError && err.httpErrorResponse) {
            status = err.httpErrorResponse.status;
        }

        if (status === null) {
            return false;
        }

        return status === 0 || status >= 500;
    }

    private openExpiryDialog(): void {
        // Never stack dialogs.
        if (this._dialogRef) {
            return;
        }

        const expiresAt = this._authSvc.getTokenExpiresAt();

        // Nothing left to count down to - just go.
        if (!expiresAt || expiresAt - (new Date().getTime() / 1000) <= 0) {
            this.logout();
            return;
        }

        this._dialogRef = this._matDialog.open(SessionExpiryDialogComponent, {
            autoFocus: false,
            disableClose: true,
            data: { expiresAt: expiresAt },
            panelClass: 'fuse-confirmation-dialog-panel',
        });

        this._dialogRef.afterClosed().subscribe((result: SessionExpiryDialogResult) => {
            this._dialogRef = null;

            // 'logout' (clicked) and 'expired' (countdown ran out) are handled the same way.
            // A close with no result comes from stop(), where the caller signs out already.
            if (result === 'logout' || result === 'expired') {
                this.logout();
            }
        });
    }

    private logout(): void {
        this.clearTimers();
        this._authSvc.signOut().subscribe(() => {
            this._authSvc.redirectToSignIn();
        });
    }

    private clearTimers(): void {
        this._timerSub?.unsubscribe();
        this._timerSub = null;
    }
}
